import { useState, useEffect } from 'react';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

export interface PWAInstallState {
  isInstallable: boolean;
  isInstalled: boolean;
  isStandalone: boolean;
  canInstall: boolean;
  platform: 'desktop' | 'mobile' | 'unknown';
}

export const usePWAInstall = () => {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [state, setState] = useState<PWAInstallState>({
    isInstallable: false,
    isInstalled: false,
    isStandalone: false,
    canInstall: false,
    platform: 'unknown'
  });

  useEffect(() => {
    // Check if app is running in standalone mode (installed)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                        (window.navigator as any).standalone === true;

    // Detect platform
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const platform = isMobile ? 'mobile' : 'desktop';

    // Check if already installed
    const isInstalled = isStandalone;

    setState(prev => ({
      ...prev,
      isStandalone,
      isInstalled,
      platform
    }));

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      const promptEvent = e as BeforeInstallPromptEvent;
      setInstallPrompt(promptEvent);
      
      setState(prev => ({
        ...prev,
        isInstallable: true,
        canInstall: !prev.isInstalled
      }));
    };

    // Listen for appinstalled event
    const handleAppInstalled = () => {
      console.log('PWA was installed');
      setInstallPrompt(null);
      setState(prev => ({
        ...prev,
        isInstalled: true,
        isInstallable: false,
        canInstall: false
      }));
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const installPWA = async (): Promise<boolean> => {
    if (!installPrompt) {
      console.warn('Install prompt not available');
      return false;
    }

    try {
      await installPrompt.prompt();
      const choiceResult = await installPrompt.userChoice;
      
      if (choiceResult.outcome === 'accepted') {
        console.log('User accepted the install prompt');
        setInstallPrompt(null);
        return true;
      } else {
        console.log('User dismissed the install prompt');
        return false;
      }
    } catch (error) {
      console.error('Error during installation:', error);
      return false;
    }
  };

  const dismissInstallPrompt = () => {
    setInstallPrompt(null);
    setState(prev => ({
      ...prev,
      canInstall: false
    }));
    
    // Store dismissal in localStorage to avoid showing again for a while
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  const shouldShowInstallPrompt = (): boolean => {
    if (state.isInstalled || !state.canInstall || !installPrompt) {
      return false;
    }

    // Check if user dismissed the prompt recently (within 7 days)
    const dismissedTime = localStorage.getItem('pwa-install-dismissed');
    if (dismissedTime) {
      const daysSinceDismissal = (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissal < 7) {
        return false;
      }
    }

    return true;
  };

  const getInstallInstructions = (): string[] => {
    const { platform } = state;
    
    if (platform === 'mobile') {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      
      if (isIOS) {
        return [
          'No Safari, toque no ícone de compartilhamento',
          'Role para baixo e toque em "Adicionar à Tela de Início"',
          'Toque em "Adicionar" para instalar o app'
        ];
      } else {
        return [
          'Toque no menu do navegador (⋮)',
          'Selecione "Adicionar à tela inicial" ou "Instalar app"',
          'Toque em "Adicionar" para confirmar'
        ];
      }
    } else {
      return [
        'Clique no ícone de instalação na barra de endereços',
        'Ou use o menu do navegador (⋮) > "Instalar Manu"',
        'Clique em "Instalar" para adicionar à área de trabalho'
      ];
    }
  };

  return {
    ...state,
    installPWA,
    dismissInstallPrompt,
    shouldShowInstallPrompt: shouldShowInstallPrompt(),
    getInstallInstructions
  };
};